import { Group } from '@mantine/core';
import { ColDef, GridApi } from 'ag-grid-community';
import { useApi } from 'api/api-context';
import DataTable from 'components/tables/DataTable';
import { DataTableExport } from 'components/tables/ExportModal';
import SimpleValueFormatter from 'components/tables/SimpleValueFormatter';
import { getEvidenceFieldValue } from 'utils/evidence-field';
import BulkCloseAction from 'components/tables/data/price-offer/actions/bulk/BulkCloseAction';
import BulkDeleteAction from 'components/tables/data/price-offer/actions/bulk/BulkDeleteAction';
import ActionsColumn from 'components/tables/data/price-offer/columns/ActionsColumn';
import AssignedToColumn from 'components/tables/data/price-offer/columns/AssignedToColumn';
import DeviceColumn from 'components/tables/data/price-offer/columns/DeviceColumn';
import DeviceIdColumn from 'components/tables/data/price-offer/columns/DeviceIdColumn';
import FileColumn from 'components/tables/data/price-offer/columns/FileColumn';
import FinalPriceColumn from 'components/tables/data/price-offer/columns/FinalPriceColumn';
import OrganizationColumn from 'components/tables/data/price-offer/columns/OrganizationColumn';
import PriceOfferIdColumn from 'components/tables/data/price-offer/columns/PriceOfferIdColumn';
import StatusColumn from 'components/tables/data/price-offer/columns/StatusColumn';
import PriceOfferDeviceSelectFilter from 'components/tables/data/price-offer/filters/PriceOfferDeviceSelectFilter';
import PriceOfferSingleValueColumnFilter from 'components/tables/data/price-offer/filters/PriceOfferSingleValueColumnFilter';
import {
  PriceOfferStatusColumnFilter,
  PriceOfferStatusFloatingFilter,
} from 'components/tables/data/price-offer/filters/PriceOfferStatusFilter';
import PriceOfferUserSelectFilter from 'components/tables/data/price-offer/filters/PriceOfferUserSelectFilter';
import { PRICE_OFFER_ACTIONS_COLUMN_WIDTH } from 'components/tables/data/price-offer/PriceOfferActions';
import { PriceOfferRow, PriceOfferStatusChangingAction } from 'components/tables/data/price-offer/types';
import useObjectColumn from 'components/tables/hierarchy/ObjectColumn';
import OrganizationMultiSelectFilter, {
  OrganizationMultiColumnFilter,
} from 'components/tables/filters/OrganizationMultiSelectFilter';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ADD_PRICE_OFFER_PAGE_PATH } from 'routes/paths';

interface PriceOfferTableProps {
  initialFilters?: Record<string, any>;
}

type EvidenceField = NonNullable<PriceOfferRow['device']['evidenceFields']>[number];

const STATUS_CHANGING_ACTION_STATUS: Record<PriceOfferStatusChangingAction, PriceOfferRow['status']> = {
  acceptable: {
    priceOfferStatusId: 3,
    priceOfferStatusName: 'Vyhovujúca',
    slug: 'acceptable',
  },
  unacceptable: {
    priceOfferStatusId: 4,
    priceOfferStatusName: 'Nevyhovujúca',
    slug: 'unacceptable',
  },
  delete: {
    priceOfferStatusId: 999,
    priceOfferStatusName: 'Zmazaná',
    slug: 'deleted',
  },
  return: {
    priceOfferStatusId: 1,
    priceOfferStatusName: 'Vytvorená',
    slug: 'created',
  },
};

/**
 * Displays price offers in the standard data table.
 */
export default function PriceOfferTable({ initialFilters }: PriceOfferTableProps) {
  const { getAction, hasPermissionAnywhere } = useApi();
  const objectColumn = useObjectColumn({
    breadcrumbField: 'department.breadcrumbs',
    organizationFilterKey: 'organization.organizationId',
  });
  const [refreshToken, setRefreshToken] = useState(0);
  const [selectedRows, setSelectedRows] = useState<PriceOfferRow[]>([]);
  const [api, setApi] = useState<GridApi<PriceOfferRow> | null>(null);
  const [evidenceFieldsColumns, setEvidenceFieldsColumns] = useState<ColDef[]>([]);
  const inventoryRequest = useRef(0);

  const action = useCallback(async () => {
    const request = ++inventoryRequest.current;
    const listPriceOffers = getAction('PriceOfferList');
    const priceOffers = await listPriceOffers();
    if (request !== inventoryRequest.current) return [];
    const evidenceFieldMap = new Map<string, EvidenceField>();

    const augmentedPriceOffers = priceOffers.map((priceOffer) => {
      const evidenceFields = priceOffer.device.evidenceFields?.reduce((acc, field) => {
        if (!field.fieldName || !field.evidenceFieldId) {
          return acc;
        }

        const key = String(field.evidenceFieldId);
        const value = getEvidenceFieldValue(field) ?? '';

        evidenceFieldMap.set(key, field);
        acc[key] = value;

        return acc;
      }, {} as Record<string, string | number | boolean>);

      return {
        ...priceOffer,
        deviceId: priceOffer.device.deviceId,
        evidenceFields,
      };
    });

    const nextEvidenceFieldsColumns = Array.from(evidenceFieldMap).map(
      ([key, { fieldName: name, precision = 0 }]) =>
        ({
          field: `evidenceFields.${key}`,
          headerName: name,
          minWidth: 200,
          sortable: true,
          unSortIcon: true,
          filter: true,
          floatingFilter: true,
          filterValueGetter: ({ data: { evidenceFields } }: { data: PriceOfferRow }) => (evidenceFields as any)?.[key],
          cellRenderer: ({ data: { evidenceFields } }: { data: PriceOfferRow }) => {
            const value = (evidenceFields as any)?.[key];
            return <SimpleValueFormatter value={value} precision={precision} />;
          },
          icons: {
            filter: () =>
              '<div title="Pokročilé filtrovanie"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-filter-spark"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M15 12.5v-.5l4.414 -4.414a2 2 0 0 0 .586 -1.414v-2.172h-16v2.227c0 .497 .185 .977 .52 1.345l4.48 4.928v8.5l2 -.667" /><path d="M18.5 22a4.75 4.75 0 0 1 3.5 -3.5a4.75 4.75 0 0 1 -3.5 -3.5a4.75 4.75 0 0 1 -3.5 3.5a4.75 4.75 0 0 1 3.5 3.5" /></svg></div>',
          },
        } satisfies ColDef)
    );

    setEvidenceFieldsColumns(nextEvidenceFieldsColumns);

    return augmentedPriceOffers as PriceOfferRow[];
  }, [getAction]);

  useEffect(
    () => () => {
      inventoryRequest.current += 1;
    },
    []
  );

  /**
   * Updates changed rows immediately and then refreshes table data from the API.
   */
  const updatePriceOffersAfterAction = useCallback(
    (actionName?: PriceOfferStatusChangingAction, priceOffers: PriceOfferRow[] = []) => {
      if (!actionName || priceOffers.length === 0) {
        setRefreshToken((value) => value + 1);
        return;
      }

      const updatedPriceOffers = priceOffers.map((priceOffer) => ({
        ...priceOffer,
        status: STATUS_CHANGING_ACTION_STATUS[actionName],
        finalPriceLabor: actionName === 'return' ? undefined : priceOffer.finalPriceLabor,
        finalPriceMaterial: actionName === 'return' ? undefined : priceOffer.finalPriceMaterial,
        finalPrice: actionName === 'return' ? undefined : priceOffer.finalPrice,
        fileId: actionName === 'return' ? undefined : priceOffer.fileId,
        actionPermissions: {
          ...priceOffer.actionPermissions,
          canProvide: false,
          canClose: false,
          canReturn: false,
          canDelete: actionName === 'return' ? priceOffer.actionPermissions.canDelete : false,
        },
      }));

      api?.applyTransaction({ update: updatedPriceOffers });
      api?.refreshCells({ force: true });
      setRefreshToken((value) => value + 1);
    },
    [api]
  );

  /**
   * Updates a single row after a price offer action.
   */
  const onChanged = useCallback(
    (actionName?: PriceOfferStatusChangingAction, priceOffer?: PriceOfferRow) => {
      updatePriceOffersAfterAction(actionName, priceOffer ? [priceOffer] : []);
    },
    [updatePriceOffersAfterAction]
  );

  /**
   * Clears selected rows and refreshes table data after a bulk action.
   */
  const onBulkActionComplete = useCallback(
    (actionName: PriceOfferStatusChangingAction, priceOffers: PriceOfferRow[]) => {
      updatePriceOffersAfterAction(actionName, priceOffers);
      api?.deselectAll();
      setSelectedRows([]);
    },
    [api, updatePriceOffersAfterAction]
  );

  const exportColumns = useMemo(
    () => [
      'priceOfferId',
      'organization.organizationId',
      ...objectColumn.columnKeys,
      'deviceId',
      'device.deviceType.deviceTypeId',
      'assignedTo.userId',
      'createdBy.userId',
      'finalPriceLabor',
      'finalPriceMaterial',
      'finalPrice',
      'status.priceOfferStatusId',
      'createdAt',
      ...evidenceFieldsColumns.map(({ field }) => field ?? '').filter(Boolean),
    ],
    [evidenceFieldsColumns, objectColumn.columnKeys]
  );

  const dataExport: DataTableExport<PriceOfferRow> = useMemo(
    () => ({
      modalTitle: 'Exportovať cenové ponuky',
      fileName: 'cenove-ponuky.xlsx',
      columnKeys: exportColumns,
      columnReplacements: objectColumn.columnReplacements,
      ready: objectColumn.exportReady,
      error: objectColumn.exportError,
      initialSelectedColumns: (rows) =>
        exportColumns.filter((column) => {
          if (column.startsWith('evidenceFields.')) {
            const fieldKey = column.replace('evidenceFields.', '');

            return rows.some((row) => {
              const value = (row.evidenceFields as any)?.[fieldKey];
              return value !== undefined && value !== null && value !== '';
            });
          }

          return true;
        }),
    }),
    [exportColumns, objectColumn.columnReplacements, objectColumn.exportError, objectColumn.exportReady]
  );

  const columns: ColDef[] = useMemo(
    () => [
      {
        field: 'priceOfferId',
        headerName: 'ID',
        width: 76,
        minWidth: 76,
        maxWidth: 76,
        resizable: false,
        sortable: true,
        unSortIcon: true,
        cellRenderer: PriceOfferIdColumn,
      },
      {
        field: 'organization.organizationId',
        headerName: 'Organizácia',
        minWidth: 220,
        filter: OrganizationMultiColumnFilter,
        floatingFilter: true,
        floatingFilterComponent: OrganizationMultiSelectFilter,
        sortable: true,
        unSortIcon: true,
        cellRenderer: OrganizationColumn,
        valueGetter: ({ data }: { data: PriceOfferRow }) => data.organization.organizationName,
      },
      ...objectColumn.columns,
      {
        field: 'deviceId',
        headerName: 'ID zariadenia',
        minWidth: 140,
        sortable: true,
        unSortIcon: true,
        cellRenderer: DeviceIdColumn,
        valueGetter: ({ data }: { data: PriceOfferRow }) => data.deviceId ?? data.device.deviceId,
      },
      {
        field: 'device.deviceId',
        headerName: 'Názov zariadenia',
        minWidth: 220,
        filter: PriceOfferSingleValueColumnFilter,
        floatingFilter: true,
        floatingFilterComponent: PriceOfferDeviceSelectFilter,
        sortable: true,
        unSortIcon: true,
        cellRenderer: DeviceColumn,
        valueGetter: ({ data }: { data: PriceOfferRow }) => data.device.deviceName,
      },
      {
        field: 'device.deviceType.deviceTypeId',
        headerName: 'Zariadenie',
        minWidth: 180,
        hide: true,
        valueGetter: ({ data }: { data: PriceOfferRow }) => data.device.deviceType.deviceTypeName,
      },
      {
        field: 'assignedTo.userId',
        headerName: 'Vypracovateľ',
        minWidth: 200,
        filter: PriceOfferSingleValueColumnFilter,
        floatingFilter: true,
        floatingFilterComponent: PriceOfferUserSelectFilter.configure({
          filterKey: 'assignedTo.userId',
          userPath: 'assignedTo',
          placeholder: 'Ktokoľvek',
        }),
        sortable: true,
        unSortIcon: true,
        cellRenderer: AssignedToColumn,
        valueGetter: ({ data }: { data: PriceOfferRow }) => data.assignedTo.fullName,
      },
      {
        field: 'createdBy.userId',
        headerName: 'Zadávateľ',
        minWidth: 200,
        filter: PriceOfferSingleValueColumnFilter,
        floatingFilter: true,
        floatingFilterComponent: PriceOfferUserSelectFilter.configure({
          filterKey: 'createdBy.userId',
          userPath: 'createdBy',
          placeholder: 'Ktokoľvek',
        }),
        hide: true,
        valueGetter: ({ data }: { data: PriceOfferRow }) => data.createdBy.fullName,
      },
      {
        field: 'finalPriceLabor',
        headerName: 'Cena práce',
        minWidth: 160,
        sortable: true,
        unSortIcon: true,
        cellRenderer: FinalPriceColumn,
        cellRendererParams: { field: 'finalPriceLabor' },
      },
      {
        field: 'finalPriceMaterial',
        headerName: 'Cena materiálu',
        minWidth: 160,
        sortable: true,
        unSortIcon: true,
        cellRenderer: FinalPriceColumn,
        cellRendererParams: { field: 'finalPriceMaterial' },
      },
      {
        field: 'finalPrice',
        headerName: 'Cena spolu',
        minWidth: 160,
        sortable: true,
        unSortIcon: true,
        cellRenderer: FinalPriceColumn,
      },
      {
        field: 'fileId',
        headerName: 'Súbor',
        width: 88,
        minWidth: 88,
        sortable: false,
        filter: false,
        cellRenderer: FileColumn,
      },
      {
        field: 'status.priceOfferStatusId',
        headerName: 'Stav',
        minWidth: 150,
        filter: PriceOfferStatusColumnFilter,
        floatingFilter: true,
        floatingFilterComponent: PriceOfferStatusFloatingFilter,
        sortable: true,
        unSortIcon: true,
        cellRenderer: StatusColumn,
        valueGetter: ({ data }: { data: PriceOfferRow }) => data.status.priceOfferStatusName,
      },
      {
        field: 'createdAt',
        headerName: 'Vytvorená',
        minWidth: 160,
        hide: true,
      },
      ...evidenceFieldsColumns,
      {
        field: '_actions',
        headerName: '',
        pinned: 'right',
        width: PRICE_OFFER_ACTIONS_COLUMN_WIDTH,
        minWidth: PRICE_OFFER_ACTIONS_COLUMN_WIDTH,
        maxWidth: PRICE_OFFER_ACTIONS_COLUMN_WIDTH,
        resizable: false,
        sortable: false,
        filter: false,
        lockPosition: 'right',
        cellRenderer: ActionsColumn,
        cellRendererParams: { onChanged },
      },
    ],
    [evidenceFieldsColumns, objectColumn.columns, onChanged]
  );

  return (
    <DataTable
      tableId="price-offers"
      title="Cenové ponuky"
      columns={columns}
      action={action}
      addButtonText="Pridať novú"
      addButtonTarget={ADD_PRICE_OFFER_PAGE_PATH.original}
      hideAddButton={!hasPermissionAnywhere('assign-price-offers')}
      hideToggleDiscarded
      defaultProfileFilters={{
        'status.priceOfferStatusId': { filter: [1, 2, 3, 4] },
      }}
      initialFilters={initialFilters}
      dataRefreshToken={refreshToken}
      selectedRows={selectedRows}
      onSelectedRowsChange={setSelectedRows}
      onReady={setApi}
      bulkActions={
        <Group spacing={16}>
          <BulkCloseAction priceOffers={selectedRows} onBulkActionComplete={onBulkActionComplete} />
          <BulkDeleteAction priceOffers={selectedRows} onBulkActionComplete={onBulkActionComplete} />
        </Group>
      }
      getRowId={({ data }) => String(data.priceOfferId)}
      dataExport={dataExport}
      profileMigrationReady={objectColumn.profileMigrationReady}
    />
  );
}
